import React, { useEffect, useState, Suspense, lazy } from 'react';
import { TopBar } from './components/TopBar';
import { HeroSection } from './components/HeroSection';
// Lazy-load below-the-fold sections to shrink initial JS bundle and speed up first paint.
// (We deliberately do NOT use `content-visibility: auto` because it causes visible
// scroll jumps when section heights don't match their reserved placeholder.)
const ProblemSection = lazy(() => import('./components/ProblemSection').then(m => ({
  default: m.ProblemSection
})));
const ProductIntro = lazy(() => import('./components/ProductIntro').then(m => ({
  default: m.ProductIntro
})));
const MethodPillars = lazy(() => import('./components/MethodPillars').then(m => ({
  default: m.MethodPillars
})));
const WhatsIncluded = lazy(() => import('./components/WhatsIncluded').then(m => ({
  default: m.WhatsIncluded
})));
const TransformationSection = lazy(() => import('./components/TransformationSection').then(m => ({
  default: m.TransformationSection
})));
const AudienceSection = lazy(() => import('./components/AudienceSection').then(m => ({
  default: m.AudienceSection
})));
const GuaranteeSection = lazy(() => import('./components/GuaranteeSection').then(m => ({
  default: m.GuaranteeSection
})));
const FinalCTA = lazy(() => import('./components/FinalCTA').then(m => ({
  default: m.FinalCTA
})));
const Footer = lazy(() => import('./components/Footer').then(m => ({
  default: m.Footer
})));
const FloatingCTA = lazy(() => import('./components/FloatingCTA').then(m => ({
  default: m.FloatingCTA
})));
const RecentPurchaseToast = lazy(() => import('./components/RecentPurchaseToast').then(m => ({
  default: m.RecentPurchaseToast
})));
const BundleUpsellPopup = lazy(() => import('./components/BundleUpsellPopup').then(m => ({
  default: m.BundleUpsellPopup
})));
// Defers mounting children until the browser is idle. Used for non-critical
// overlays (floating CTA, toast, popup) so they don't compete with first paint.
function DeferredMount({
  children
}: {
  children: React.ReactNode;
}) {
  const [ready, setReady] = useState(false);
  useEffect(() => {
    const ric = (window as any).requestIdleCallback as ((cb: () => void, opts?: {
      timeout: number;
    }) => number) | undefined;
    let id: any;
    if (ric) {
      id = ric(() => setReady(true), {
        timeout: 3000
      });
    } else {
      id = setTimeout(() => setReady(true), 1500);
    }
    return () => {
      if (ric && (window as any).cancelIdleCallback) {
        ;
        (window as any).cancelIdleCallback(id);
      } else {
        clearTimeout(id);
      }
    };
  }, []);
  if (!ready) return null;
  return <>{children}</>;
}
export function App() {
  return <div className="min-h-screen w-full" data-id="element-0">
      <TopBar data-id="element-1" />
      <HeroSection data-id="element-2" />
      <Suspense fallback={null} data-id="element-3">
        {/* Non-critical overlays: mount only after the browser is idle */}
        <DeferredMount data-id="element-4">
          <FloatingCTA data-id="element-5" />
          <RecentPurchaseToast data-id="element-6" />
          <BundleUpsellPopup data-id="element-7" />
        </DeferredMount>
        {/* Below-the-fold sections (rendered immediately so React begins fetching their chunks) */}
        <ProblemSection data-id="element-8" />
        <ProductIntro data-id="element-9" />
        <MethodPillars data-id="element-10" />
        <WhatsIncluded data-id="element-11" />
        <TransformationSection data-id="element-12" />
        <AudienceSection data-id="element-13" />
        <GuaranteeSection data-id="element-14" />
        <FinalCTA data-id="element-15" />
        <Footer data-id="element-16" />
      </Suspense>
    </div>;
}